import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Keeps a failed view from taking the whole application down.
 *
 * React unmounts the entire tree when a render throws, so without this a single
 * bad record blanks the page — no navigation, no message, nothing to indicate
 * whether the service is down or the browser tripped over one field. The
 * boundary sits inside the layout so the sidebar survives and the reader can
 * move to a view that still works.
 *
 * It catches render-time failures only. A failed fetch is not an exception here
 * and is already reported per view by `ErrorPanel`; the two are deliberately
 * separate, because "this request failed" and "this view cannot be drawn" are
 * different things to tell someone.
 *
 * `resetKey` clears the error when it changes — the route path, in practice, so
 * navigating away from a broken view is enough to recover without a reload.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidUpdate(previous: { children: ReactNode; resetKey?: string }): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The component stack is the only part that says *where* it broke, and it
    // is absent from the error itself.
    console.error('View failed to render', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <section className="state-panel state-panel--error">
        <strong>This view could not be displayed</strong>
        <span>{error.message}</span>
        <button type="button" onClick={() => this.setState({ error: null })}>Try again</button>
      </section>
    );
  }
}
